import { useParams } from 'react-router'

// Constants
import { RouteParams } from '@sketch/constants'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
// eslint-disable-next-line no-restricted-imports
import { Roles } from '@sketch/gql-types/expansive'

import {
  AnalyticsEventName,
  AnalyticsEventPropsOf,
  AnalyticsEventNameWithoutProps,
  AnalyticsEventNameWithProps,
} from 'modules/analytics/plausible/types'

// Utils
import { trackEvent as originalTrackEvent } from 'modules/analytics'

// Operations
import { useGetWorkspaces } from 'modules/workspace/operations'

const getUserRole = (workspace: WorkspaceMinimalFragment): Roles => {
  if (workspace.userRole === 'ADMIN') return 'ADMIN'

  return workspace.userCanEdit ? 'EDITOR' : 'VIEWER'
}

const composeEventValue = <TEventName extends AnalyticsEventName>(
  target: string,
  type: string,
  props: AnalyticsEventPropsOf<TEventName> & {
    target?: string
    section?: string
  }
) =>
  props?.section
    ? `${type} - ${props.section}: ${target}`
    : `${type} - ${target}`

export const useTrackEvent = () => {
  const { workspaceId } = useParams<RouteParams<'WORKSPACE_DISCOVER'>>()
  const { workspaces } = useGetWorkspaces()
  const currentWorkspace =
    workspaces?.find(({ identifier }) => workspaceId === identifier) ||
    // This function can be accessed using WORKSPACE_AGNOSTIC_* route
    // therefore, if there is no currently selected workspace - use first one
    // from the list (if there are any items in the list)
    workspaces?.[0]

  const trackEvent = <TEventName extends AnalyticsEventName>(
    eventName: TEventName,
    eventProps?: TEventName extends AnalyticsEventNameWithProps
      ? AnalyticsEventPropsOf<TEventName> & { target?: string; type?: string }
      : never
  ) => {
    if (!currentWorkspace) return

    if (eventProps && eventProps.target && eventProps.type) {
      const { target, type, ...newEventProps } = eventProps

      originalTrackEvent(eventName as AnalyticsEventNameWithProps, {
        ...newEventProps,
        type: composeEventValue(target, type, newEventProps),
        role: composeEventValue(
          target,
          getUserRole(currentWorkspace),
          newEventProps
        ),
      })
      return
    }

    originalTrackEvent(eventName as AnalyticsEventNameWithoutProps)
  }

  return { trackEvent }
}
